import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';

// Fix Leaflet default marker icon issue in React/Webpack
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl,
    iconRetinaUrl,
    shadowUrl,
});

// Custom icons
const createIcon = (color) => L.divIcon({
    className: 'custom-marker',
    html: `<div style="
        background: ${color};
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
});

const userIcon = createIcon('#3b82f6'); // Blue
const cafeIcon = createIcon('#16a34a'); // Green
const partnerIcon = createIcon('#f97316'); // Orange

// Auto-fit bounds component
const FitBounds = ({ bounds }) => {
    const map = useMap();
    React.useEffect(() => {
        if (bounds && bounds.length > 0) {
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [map, bounds]);
    return null;
};

/**
 * Reusable Leaflet Map Component
 * 
 * @param {Object} props
 * @param {[number, number]} props.center - [lat, lng] center of map
 * @param {number} props.zoom - Initial zoom level (default: 13)
 * @param {Array} props.markers - Array of { id, lat, lng, name, info, type }
 * @param {Object} props.userLocation - { latitude, longitude } user's location
 * @param {Object} props.cafeLocation - { latitude, longitude } cafe location
 * @param {Function} props.onMarkerClick - Callback when marker is clicked
 * @param {string} props.height - Map height (default: '300px')
 * @param {boolean} props.showDeliveryZones - Show delivery zone circles
 */
const LeafletMap = ({
    center,
    zoom = 13,
    markers = [],
    userLocation,
    cafeLocation,
    onMarkerClick,
    height = '300px',
    showDeliveryZones = false,
}) => {
    // Calculate bounds to fit all markers
    const allPoints = [
        ...(userLocation ? [[userLocation.latitude, userLocation.longitude]] : []),
        ...(cafeLocation ? [[cafeLocation.latitude, cafeLocation.longitude]] : []),
        ...markers.map(m => [m.lat, m.lng]),
    ];

    const defaultCenter = center ||
        (userLocation ? [userLocation.latitude, userLocation.longitude] :
            (cafeLocation ? [cafeLocation.latitude, cafeLocation.longitude] :
                [-7.4148434, 109.2723232])); // Fallback to Semesta Cafe

    return (
        <MapContainer
            center={defaultCenter}
            zoom={zoom}
            style={{ height, width: '100%', borderRadius: '12px' }}
            scrollWheelZoom={false}
        >
            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* Auto-fit bounds */}
            {allPoints.length > 1 && <FitBounds bounds={allPoints} />}

            {/* Delivery zone circles (from cafe) */}
            {showDeliveryZones && cafeLocation && (
                <>
                    <Circle center={[cafeLocation.latitude, cafeLocation.longitude]} radius={1000} pathOptions={{ color: '#16a34a', fillOpacity: 0.1, weight: 1 }} />
                    <Circle center={[cafeLocation.latitude, cafeLocation.longitude]} radius={3100} pathOptions={{ color: '#eab308', fillOpacity: 0.05, weight: 1 }} />
                    <Circle center={[cafeLocation.latitude, cafeLocation.longitude]} radius={5000} pathOptions={{ color: '#f97316', fillOpacity: 0.05, weight: 1 }} />
                    <Circle center={[cafeLocation.latitude, cafeLocation.longitude]} radius={7000} pathOptions={{ color: '#ef4444', fillOpacity: 0.05, weight: 1 }} />
                </>
            )}

            {/* Cafe marker */}
            {cafeLocation && (
                <Marker position={[cafeLocation.latitude, cafeLocation.longitude]} icon={cafeIcon}>
                    <Popup>
                        <strong>☕ Semesta Cafe</strong>
                    </Popup>
                </Marker>
            )}

            {/* User location marker */}
            {userLocation && (
                <Marker position={[userLocation.latitude, userLocation.longitude]} icon={userIcon}>
                    <Popup>
                        <strong>📍 Lokasi Anda</strong>
                    </Popup>
                </Marker>
            )}

            {/* Partner/custom markers */}
            {markers.map((marker) => (
                <Marker
                    key={marker.id}
                    position={[marker.lat, marker.lng]}
                    icon={partnerIcon}
                    eventHandlers={{
                        click: () => onMarkerClick && onMarkerClick(marker),
                    }}
                >
                    <Popup>
                        <div>
                            <strong>{marker.name}</strong>
                            {marker.info && <p className="text-xs text-gray-500 mt-1">{marker.info}</p>}
                        </div>
                    </Popup>
                </Marker>
            ))}
        </MapContainer>
    );
};

export default LeafletMap;
